import * as tslib_1 from "tslib";
import { EvalStack, Value, Record, Selector, Form, RecordScope, Random } from '@swim/recon';
import { DownlinkScope, client as globalClient } from '@swim/client';
import { UiFunc } from './ui';
var ModelParam = (function () {
    function ModelParam(name, selector) {
        this._name = name;
        this._selector = selector;
    }
    ModelParam.prototype.evaluate = function (state, stack) {
        var value = this._selector.evaluate(stack).toValue();
        if (value.isDefined()) {
            state.slot(this._name, value);
        }
    };
    return ModelParam;
}());
export { ModelParam };
var Model = (function () {
    function Model(id) {
        if (id === void 0) { id = Random.generateId(); }
        this._id = id;
        this._parentModel = null;
        this._childModels = [];
        this._directives = [];
        this._appModel = null;
        this._modelScope = new ModelScope(this);
        this._state = Value.Absent;
        this._flags = 0;
    }
    Object.defineProperty(Model.prototype, "id", {
        get: function () {
            return this._id;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Model.prototype, "parentModel", {
        get: function () {
            return this._parentModel;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Model.prototype, "childModels", {
        get: function () {
            return this._childModels;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Model.prototype, "appModel", {
        get: function () {
            var appModel = this._appModel;
            if (appModel === null) {
                var model = this;
                while (model) {
                    if (model instanceof Model.AppModel) {
                        this._appModel = appModel = model;
                        break;
                    }
                    model = model._parentModel;
                }
            }
            return appModel;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Model.prototype, "directives", {
        get: function () {
            return this._directives;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Model.prototype, "modelScope", {
        get: function () {
            return this._modelScope;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Model.prototype, "routeScope", {
        get: function () {
            var appModel = this.appModel;
            return appModel ? appModel._routeScope : null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Model.prototype, "parentScope", {
        get: function () {
            var parentModel = this._parentModel;
            return parentModel ? parentModel._modelScope : null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Model.prototype, "downlinkContext", {
        get: function () {
            var model = this;
            while (model) {
                if (model._downlinkContext) {
                    return model._downlinkContext;
                }
                model = model._parentModel;
            }
            return globalClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Model.prototype, "params", {
        get: function () {
            return this.constructor.Params;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Model.prototype, "state", {
        get: function () {
            return this._state;
        },
        enumerable: true,
        configurable: true
    });
    Model.prototype.setState = function (newState) {
        newState = Value.fromAny(newState);
        var oldState = this._state;
        newState = this.willSetState(newState, oldState);
        if (!oldState.equals(newState)) {
            this._state = newState.commit();
            this.didSetState(newState, oldState);
            var appModel = this.appModel;
            if (appModel) {
                appModel.setState(newState, this);
            }
        }
    };
    Model.prototype.willSetState = function (newState, oldState) {
        return newState;
    };
    Model.prototype.didSetState = function (newState, oldState) {
    };
    Model.prototype.setParentModel = function (parentModel) {
        var oldParentModel = this._parentModel;
        if (parentModel) {
            if (oldParentModel) {
                throw new Error();
            }
            this._parentModel = parentModel;
            var appModel = this.appModel;
            if (appModel) {
                appModel.addDescendantModels(this, parentModel);
            }
            this.requireRestate();
            this.didSetParentModel(parentModel);
        }
        else if (oldParentModel) {
            var appModel = oldParentModel.appModel;
            if (appModel) {
                appModel.removeDescendantModels(this, oldParentModel);
            }
            this._flags = 0;
            this._appModel = null;
            this._parentModel = null;
            this.didSetParentModel(null);
        }
    };
    Model.prototype.didSetParentModel = function (parentModel) {
    };
    Model.prototype.setAppModel = function (appModel) {
        this._appModel = appModel;
        if (appModel === null) {
            this._modelScope.close();
        }
        this.didSetAppModel(appModel);
    };
    Model.prototype.didSetAppModel = function (appModel) {
    };
    Model.prototype.appendChildModel = function (childModel) {
        if (!childModel) {
            throw new TypeError('' + childModel);
        }
        else if (this._childModels.indexOf(childModel) >= 0) {
            throw new Error('duplicate child');
        }
        else if (childModel._parentModel) {
            throw new Error('duplicate parent');
        }
        this._childModels.push(childModel);
        childModel.setParentModel(this);
        this.didInsertChildModel(childModel);
    };
    Model.prototype.prependChildModel = function (childModel) {
        if (!childModel) {
            throw new TypeError('' + childModel);
        }
        else if (this._childModels.indexOf(childModel) >= 0) {
            throw new Error('duplicate child');
        }
        else if (childModel._parentModel) {
            throw new Error('duplicate parent');
        }
        this._childModels.unshift(childModel);
        childModel.setParentModel(this);
        this.didInsertChildModel(childModel);
    };
    Model.prototype.insertChildModel = function (childModel, targetModel) {
        if (!childModel) {
            throw new TypeError('' + childModel);
        }
        else if (this._childModels.indexOf(childModel) >= 0) {
            throw new Error('duplicate child');
        }
        else if (childModel._parentModel) {
            throw new Error('duplicate parent');
        }
        var i = targetModel ? this._childModels.indexOf(targetModel) : -1;
        if (i < 0) {
            i = this._childModels.length;
        }
        this._childModels.splice(i, 0, childModel);
        childModel.setParentModel(this);
        this.didInsertChildModel(childModel);
    };
    Model.prototype.removeChildModel = function (childModel) {
        if (!childModel) {
            throw new TypeError('' + childModel);
        }
        var i = this._childModels.indexOf(childModel);
        if (i >= 0) {
            childModel.setParentModel(null);
            this._childModels.splice(i, 1);
            this.didRemoveChildModel(childModel);
        }
    };
    Model.prototype.didInsertChildModel = function (childModel) {
        for (var i = 0, n = this._directives.length; i < n; i += 1) {
            this._directives[i].didInsertChildModel(childModel);
        }
        if ((childModel._flags & Model.REQUEST_RESTATE) !== 0) {
            this.requestRestate();
        }
    };
    Model.prototype.didRemoveChildModel = function (childModel) {
        for (var i = 0, n = this._directives.length; i < n; i += 1) {
            this._directives[i].didRemoveChildModel(childModel);
        }
    };
    Model.prototype.addDirective = function (directive) {
        if (!directive) {
            throw new TypeError('' + directive);
        }
        else if (this._directives.indexOf(directive) >= 0) {
            throw new Error('duplicate directive');
        }
        else if (directive._model) {
            throw new Error('duplicate model');
        }
        this._directives.push(directive);
        directive.setModel(this);
        this.didAddDirective(directive);
    };
    Model.prototype.removeDirective = function (directive) {
        if (!directive) {
            throw new TypeError('' + directive);
        }
        var i = this._directives.indexOf(directive);
        if (i >= 0) {
            directive.setModel(null);
            this._directives.splice(i, 1);
            this.didRemoveDirective(directive);
        }
    };
    Model.prototype.didAddDirective = function (directive) {
    };
    Model.prototype.didRemoveDirective = function (directive) {
    };
    Model.prototype.appendChildView = function (childId, viewDef) {
        var appModel = this._appModel;
        if (appModel) {
            appModel.appendChildView(this._id, childId, viewDef);
        }
    };
    Model.prototype.prependChildView = function (childId, viewDef) {
        var appModel = this._appModel;
        if (appModel) {
            appModel.prependChildView(this._id, childId, viewDef);
        }
    };
    Model.prototype.insertChildView = function (childId, targetId, viewDef) {
        var appModel = this._appModel;
        if (appModel) {
            appModel.insertChildView(this._id, childId, targetId, viewDef);
        }
    };
    Model.prototype.removeChildView = function (childId) {
        var appModel = this._appModel;
        if (appModel) {
            appModel.removeChildView(this._id, childId);
        }
    };
    Model.prototype.isSealed = function (sealed) {
        if (sealed === undefined) {
            return (this._flags & Model.SEALED) !== 0;
        }
        else {
            if (sealed) {
                this._flags |= Model.SEALED;
            }
            else {
                this._flags &= ~Model.SEALED;
            }
            return this;
        }
    };
    Model.prototype.willRemodel = function (state, stack) {
        for (var i = 0, n = this._directives.length; i < n; i += 1) {
            this._directives[i].willRemodel(state, stack);
        }
    };
    Model.prototype.didRemodel = function (state, stack) {
        for (var i = 0, n = this._directives.length; i < n; i += 1) {
            this._directives[i].didRemodel(state, stack);
        }
    };
    Model.prototype.remodel = function (stack) {
        if (stack === void 0) { stack = this.stack(); }
        var params = this.params;
        var state = Record.empty(params.length);
        for (var i = 0; i < params.length; i += 1) {
            params[i].evaluate(state, stack);
        }
        this.willRemodel(state, stack);
        this.setState(state);
        this.didRemodel(state, stack);
    };
    Model.prototype.willRestate = function () {
        for (var i = 0, n = this._directives.length; i < n; i += 1) {
            this._directives[i].willRestate();
        }
    };
    Model.prototype.didRestate = function () {
        this._modelScope.materializeScope();
        for (var i = 0, n = this._directives.length; i < n; i += 1) {
            this._directives[i].didRestate();
        }
        this.remodel();
    };
    Model.prototype.requestRestate = function () {
        if ((this._flags & Model.REQUEST_RESTATE) === 0) {
            this._flags |= Model.REQUEST_RESTATE;
            if (this._parentModel) {
                this._parentModel.requestRestate();
            }
        }
    };
    Model.prototype.requireRestate = function (source) {
        if (source === void 0) { source = this; }
        if ((this._flags & Model.REQUIRE_RESTATE) === 0) {
            this._flags |= Model.REQUIRE_RESTATE;
            this.requestRestate();
            if (source === this || (this._flags & Model.SEALED) === 0) {
                var childModels = this._childModels;
                for (var i = 0, n = childModels.length; i < n; i += 1) {
                    childModels[i].requireRestate(source);
                }
            }
        }
    };
    Model.prototype.cascadeWillRestate = function () {
        if ((this._flags & Model.REQUEST_RESTATE) !== 0) {
            if ((this._flags & Model.REQUIRE_RESTATE) !== 0) {
                this.willRestate();
            }
            var childModels = this._childModels;
            for (var i = 0, n = childModels.length; i < n; i += 1) {
                childModels[i].cascadeWillRestate();
            }
        }
    };
    Model.prototype.cascadeDidRestate = function () {
        if ((this._flags & Model.REQUEST_RESTATE) !== 0) {
            this._flags &= ~Model.REQUEST_RESTATE;
            var childModels = this._childModels;
            for (var i = 0, n = childModels.length; i < n; i += 1) {
                childModels[i].cascadeDidRestate();
            }
            if ((this._flags & Model.REQUIRE_RESTATE) !== 0) {
                this._flags &= ~Model.REQUIRE_RESTATE;
                this.didRestate();
            }
        }
    };
    Model.prototype.setData = function (data) {
        if (typeof data === 'string') {
            data = Record.parseRecon(data);
        }
        else if (!(data instanceof Record)) {
            data = Record.of(data);
        }
        for (var i = 0, n = this._directives.length; i < n; i += 1) {
            this._directives[i].setData(data);
        }
        this._modelScope._record = data;
        this.requireRestate();
    };
    Model.prototype.initDownlink = function (init, stack) {
        if (stack === void 0) { stack = this.stack(); }
        if (!init || init.hostUri === undefined) {
            init = init || {};
            init.hostUri = MODEL_HOST_SELECTOR.evaluate(stack).cast(Form.Uri);
        }
        if (!init || init.nodeUri === undefined) {
            init = init || {};
            init.nodeUri = MODEL_NODE_SELECTOR.evaluate(stack).cast(Form.Uri);
        }
        if (!init || init.laneUri === undefined) {
            init = init || {};
            init.laneUri = MODEL_LANE_SELECTOR.evaluate(stack).cast(Form.Uri);
        }
        if (!init || init.prio === undefined) {
            init = init || {};
            init.prio = MODEL_PRIO_SELECTOR.evaluate(stack).numberValue();
        }
        if (!init || init.rate === undefined) {
            init = init || {};
            init.rate = MODEL_RATE_SELECTOR.evaluate(stack).numberValue();
        }
        return init;
    };
    Model.prototype.stack = function () {
        return EvalStack.of(Model.GlobalScope, this._modelScope);
    };
    Model.Params = [];
    Model.GlobalScope = UiFunc.GlobalScope;
    Model.REQUEST_RESTATE = 1 << 0;
    Model.REQUIRE_RESTATE = 1 << 1;
    Model.SEALED = 1 << 8;
    Model.AppModel = undefined;
    return Model;
}());
export { Model };
var ModelScope = (function (_super) {
    tslib_1.__extends(ModelScope, _super);
    function ModelScope(model) {
        var _this = _super.call(this) || this;
        _this._model = model;
        return _this;
    }
    Object.defineProperty(ModelScope.prototype, "parentScope", {
        get: function () {
            return this._model.parentScope;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ModelScope.prototype, "downlinkContext", {
        get: function () {
            return this._model.downlinkContext;
        },
        enumerable: true,
        configurable: true
    });
    ModelScope.prototype.initDownlink = function (init, stack) {
        return this._model.initDownlink(init, stack);
    };
    ModelScope.prototype.materializeMutableScope = function (value, stack) {
        var scope = new MutableScope(this._model);
        value.header('mutable').forEach(function (item) {
            if (item instanceof Selector) {
                scope._dependents.push(item);
            }
        }, this);
        scope._record = value.tail();
        return scope;
    };
    ModelScope.prototype.materializeValue = function (value, stack) {
        if (value.tag === 'mutable') {
            var scope = this.materializeMutableScope(value, stack);
            if (scope instanceof MutableScope) {
                return scope;
            }
        }
        return _super.prototype.materializeValue.call(this, value, stack);
    };
    ModelScope.prototype.didChange = function (newItem, oldItem, index) {
        this._model.requireRestate();
    };
    ModelScope.prototype.stack = function () {
        return EvalStack.of(Model.GlobalScope, this);
    };
    return ModelScope;
}(DownlinkScope));
export { ModelScope };
var RouteScope = (function (_super) {
    tslib_1.__extends(RouteScope, _super);
    function RouteScope(model) {
        var _this = _super.call(this) || this;
        _this._model = model;
        return _this;
    }
    Object.defineProperty(RouteScope.prototype, "parentScope", {
        get: function () {
            return null;
        },
        enumerable: true,
        configurable: true
    });
    RouteScope.prototype.didChange = function (newItem, oldItem, index) {
        this._model.requireRestate();
    };
    RouteScope.prototype.stack = function () {
        return EvalStack.of(Model.GlobalScope, this);
    };
    return RouteScope;
}(RecordScope));
export { RouteScope };
var MutableScope = (function (_super) {
    tslib_1.__extends(MutableScope, _super);
    function MutableScope(model) {
        var _this = _super.call(this) || this;
        _this._model = model;
        _this._dependents = Record.empty();
        return _this;
    }
    Object.defineProperty(MutableScope.prototype, "parentScope", {
        get: function () {
            return null;
        },
        enumerable: true,
        configurable: true
    });
    MutableScope.prototype.didChange = function (newItem, oldItem, index) {
        this._model.requireRestate();
    };
    MutableScope.prototype.stack = function () {
        return EvalStack.of(Model.GlobalScope, this);
    };
    return MutableScope;
}(RecordScope));
export { MutableScope };
var MODEL_HOST_SELECTOR = Selector.get('host');
var MODEL_NODE_SELECTOR = Selector.get('node');
var MODEL_LANE_SELECTOR = Selector.get('lane');
var MODEL_PRIO_SELECTOR = Selector.get('prio');
var MODEL_RATE_SELECTOR = Selector.get('rate');
//# sourceMappingURL=data:application/json;base64,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